<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import { HgtUndAtd } from '@/models/HgtUndAtd';
import { createValidator, createValidators, validateNotEmpty, validateNumberBetween } from '@/helpers/model_validators';
import { Nutzungsart, NutzungsartCollection } from '@/models/Nutzungsart';
import KpNumberField from '@/components/KpNumberField.vue';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import KpJahrSelect from '@/components/KpJahrSelect.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<HgtUndAtd | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    jahr: createValidator(
        computed(() => record.value?.jahr),
        [validateNotEmpty],
    ),
    kanton: createValidator(
        computed(() => record.value?.kanton_id),
        [validateNotEmpty],
    ),
    heizgradtage: createValidator(
        computed(() => record.value?.heizgradtage),
        [validateNotEmpty],
    ),
    gewichtung_heizgradtage: createValidator(
        computed(() => record.value?.gewichtung_heizgradtage || null),
        [validateNotEmpty],
    ),
    akkumulierte_temperaturdifferenzen: createValidator(
        computed(() => record.value?.akkumulierte_temperaturdifferenzen),
        [validateNotEmpty],
    ),
    gewichtung_akkumulierte_temperaturdifferenzen: createValidator(
        computed(() => record.value?.gewichtung_akkumulierte_temperaturdifferenzen || null),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(HgtUndAtd);
    } else {
        try {
            const rec = await HgtUndAtd.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_jw_hgt_und_atd_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_jw_hgt_und_atd' });
    }
}

const nutzungsarten: Ref<Nutzungsart[]> = ref([]);
onMounted(async () => {
    const col = new NutzungsartCollection();
    col.setQueryParams({
        sort: [
            {
                property: 'reihenfolge',
                direction: 'asc',
            },
        ],
    });
    await col.query();
    col.each((m) => nutzungsarten.value.push(m));
});
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('hgt_und_atd') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpMandantKantonSelect
                        v-model="record.kanton_id"
                        class="col-6"
                        fluid
                        :label="$t('kanton')"
                        :invalid="!validators.kanton.isValid.value"
                        :error="validators.kanton.msg.value"
                    ></KpMandantKantonSelect>
                    <KpJahrSelect
                        v-model="record.jahr"
                        class="col-6"
                        fluid
                        :label="$t('jahr')"
                        :invalid="!validators.jahr.isValid.value"
                        :error="validators.jahr.msg.value"
                    ></KpJahrSelect>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.heizgradtage"
                        class="col-6"
                        fluid
                        :label="$t('heizgradtage')"
                        :invalid="!validators.heizgradtage.isValid.value"
                        :error="validators.heizgradtage.msg.value"
                    ></KpNumberField>
                    <KpNumberField
                        v-model="record.gewichtung_heizgradtage"
                        class="col-6"
                        fluid
                        :label="$t('gewichtung_heizgradtage')"
                        :invalid="!validators.gewichtung_heizgradtage.isValid.value"
                        :error="validators.gewichtung_heizgradtage.msg.value"
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.akkumulierte_temperaturdifferenzen"
                        class="col-6"
                        fluid
                        :label="$t('akkumulierte_temperaturdifferenzen')"
                        :invalid="!validators.akkumulierte_temperaturdifferenzen.isValid.value"
                        :error="validators.akkumulierte_temperaturdifferenzen.msg.value"
                    ></KpNumberField>
                    <KpNumberField
                        v-model="record.gewichtung_akkumulierte_temperaturdifferenzen"
                        class="col-6"
                        fluid
                        :label="$t('gewichtung_akkumulierte_temperaturdifferenzen')"
                        :invalid="!validators.gewichtung_akkumulierte_temperaturdifferenzen.isValid.value"
                        :error="validators.gewichtung_akkumulierte_temperaturdifferenzen.msg.value"
                    ></KpNumberField>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
